/**
 * Scroll Reveal Animation System
 *
 * Data-attribute-driven scroll animations powered by GSAP + ScrollTrigger.
 * Add a data-scroll-animation attribute to any element to animate it into
 * view on scroll. Respects prefers-reduced-motion.
 *
 *
 * Animation types:
 *   fade-up, fade-down, fade-left, fade-right, scale-in, fade
 *
 *
 * Data attributes:
 *   data-scroll-animation="<type>"       Animation type (required)
 *   data-scroll-duration="1.5"           Duration in seconds (default: 1)
 *   data-scroll-delay="0.2"              Delay in seconds (default: 0)
 *   data-scroll-ease="power2.inOut"      GSAP ease string (default: power3.out)
 *   data-scroll-repeat                   Reverse and replay on every scroll (default: play once)
 *   data-scroll-trigger="#selector"       Custom trigger element (default: self)
 *   data-scroll-start="top center"       ScrollTrigger start position (default: top bottom-=100px)
 *   data-scroll-stagger="group-name"     Stagger group ID — matching elements animate sequentially
 *   data-scroll-stagger-amount="0.2"     Delay between stagger items in seconds (default: 0.15)
 *
 *
 * Usage examples:
 *
 *   <!-- Basic fade up -->
 *   <div data-scroll-animation="fade-up">...</div>
 *
 *   <!-- Reverse and replay every time it scrolls in/out of view -->
 *   <div data-scroll-animation="fade-up" data-scroll-repeat>...</div>
 *
 *   <!-- Custom duration and delay -->
 *   <div data-scroll-animation="fade-left" data-scroll-duration="1.5" data-scroll-delay="0.2">...</div>
 *
 *   <!-- Stagger group — children animate one after another -->
 *   <div data-scroll-animation="fade-up" data-scroll-stagger="cards">Card 1</div>
 *   <div data-scroll-animation="fade-up" data-scroll-stagger="cards">Card 2</div>
 *   <div data-scroll-animation="fade-up" data-scroll-stagger="cards">Card 3</div>
 *
 *   <!-- Custom trigger element -->
 *   <section id="hero">
 *     <h1 data-scroll-animation="fade-up" data-scroll-trigger="#hero">...</h1>
 *   </section>
 *
 *
 * JS API:
 *   scrollAnimations.create()          Initialize all elements on the page
 *   scrollAnimations.init(container)   Initialize within a container (for htmx/dynamic content)
 *   scrollAnimations.destroy()         Kill all triggers and clean up
 *   scrollAnimations.refresh()         Recalculate ScrollTrigger positions
 */

import { gsap, ScrollTrigger } from './gsap-config.js';

const ANIMATION_STATES = {
    'fade-up':    { y: 50, autoAlpha: 0 },
    'fade-down':  { y: -50, autoAlpha: 0 },
    'fade-left':  { x: 50, autoAlpha: 0 },
    'fade-right': { x: -50, autoAlpha: 0 },
    'scale-in':   { scale: 0.9, autoAlpha: 0 },
    'fade':       { autoAlpha: 0 },
};

const triggers = [];
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function getInitialState(type) {
    const state = { ...ANIMATION_STATES[type] };
    if (!state) return { autoAlpha: 0 };

    if (prefersReducedMotion.matches) {
        delete state.x;
        delete state.y;
        delete state.scale;
    }

    return state;
}

function getSettings(el) {
    const type = el.getAttribute('data-scroll-animation');
    const duration = parseFloat(el.getAttribute('data-scroll-duration')) || 1;
    const delay = parseFloat(el.getAttribute('data-scroll-delay')) || 0;
    const ease = el.getAttribute('data-scroll-ease') || 'power3.out';
    const once = !el.hasAttribute('data-scroll-repeat');
    const triggerSelector = el.getAttribute('data-scroll-trigger');
    const start = el.getAttribute('data-scroll-start') || 'top bottom-=100px';
    const staggerGroup = el.getAttribute('data-scroll-stagger');
    const staggerAmount = parseFloat(el.getAttribute('data-scroll-stagger-amount')) || 0.15;

    return { type, duration: prefersReducedMotion.matches ? Math.min(duration, 0.5) : duration, delay, ease, once, triggerSelector, start, staggerGroup, staggerAmount };
}

function initElement(el) {
    if (el.hasAttribute('data-scroll-initialized')) return;

    const { type, duration, delay, ease, once, triggerSelector, start } = getSettings(el);
    const initialState = getInitialState(type);
    if (!initialState) return;

    const triggerEl = triggerSelector ? document.querySelector(triggerSelector) : el;

    gsap.set(el, { ...initialState, willChange: 'transform, opacity', force3D: true });
    el.setAttribute('data-scroll-initialized', '');

    const toVars = {
        ...Object.fromEntries(Object.keys(initialState).map(k => [k, k === 'autoAlpha' ? 1 : (k === 'scale' ? 1 : 0)])),
        duration,
        delay,
        ease,
        force3D: true,
    };

    const scrollTriggerConfig = {
        trigger: triggerEl,
        start,
        toggleActions: once ? 'play none none none' : 'play none none reverse',
    };

    if (once) {
        toVars.onComplete = () => {
            gsap.set(el, { willChange: 'auto', force3D: 'auto' });
            el.setAttribute('data-scroll-complete', '');
        };
    } else {
        scrollTriggerConfig.onLeaveBack = () => {
            gsap.set(el, { willChange: 'auto' });
        };
        scrollTriggerConfig.onEnter = () => {
            gsap.set(el, { willChange: 'transform, opacity' });
        };
        scrollTriggerConfig.onEnterBack = () => {
            gsap.set(el, { willChange: 'transform, opacity' });
        };
    }

    toVars.scrollTrigger = scrollTriggerConfig;

    const tween = gsap.to(el, toVars);
    triggers.push(tween.scrollTrigger);
}

function initStaggerGroup(groupName, elements) {
    const first = elements[0];
    const { type, duration, delay, ease, once, triggerSelector, start, staggerAmount } = getSettings(first);
    const initialState = getInitialState(type);
    if (!initialState) return;

    const triggerEl = triggerSelector ? document.querySelector(triggerSelector) : first;

    elements.forEach(el => {
        if (el.hasAttribute('data-scroll-initialized')) return;
        gsap.set(el, { ...initialState, willChange: 'transform, opacity', force3D: true });
        el.setAttribute('data-scroll-initialized', '');
    });

    const toVars = {
        ...Object.fromEntries(Object.keys(initialState).map(k => [k, k === 'autoAlpha' ? 1 : (k === 'scale' ? 1 : 0)])),
        duration,
        delay,
        ease,
        force3D: true,
        stagger: staggerAmount,
    };

    const scrollTriggerConfig = {
        trigger: triggerEl,
        start,
        toggleActions: once ? 'play none none none' : 'play none none reverse',
    };

    if (once) {
        toVars.onComplete = function () {
            elements.forEach(el => {
                gsap.set(el, { willChange: 'auto', force3D: 'auto' });
                el.setAttribute('data-scroll-complete', '');
            });
        };
    } else {
        scrollTriggerConfig.onLeaveBack = () => {
            elements.forEach(el => gsap.set(el, { willChange: 'auto' }));
        };
        scrollTriggerConfig.onEnter = () => {
            elements.forEach(el => gsap.set(el, { willChange: 'transform, opacity' }));
        };
        scrollTriggerConfig.onEnterBack = () => {
            elements.forEach(el => gsap.set(el, { willChange: 'transform, opacity' }));
        };
    }

    toVars.scrollTrigger = scrollTriggerConfig;

    const tween = gsap.to(elements, toVars);
    triggers.push(tween.scrollTrigger);
}

function init(container) {
    const scope = container || document;
    const elements = gsap.utils.toArray('[data-scroll-animation]', scope);

    // Separate stagger groups from standalone elements
    const staggerGroups = new Map();
    const standalone = [];

    elements.forEach(el => {
        if (el.hasAttribute('data-scroll-initialized')) return;

        const groupName = el.getAttribute('data-scroll-stagger');
        if (groupName) {
            const parent = el.parentElement;
            if (!staggerGroups.has(parent)) staggerGroups.set(parent, {});
            const parentGroups = staggerGroups.get(parent);
            if (!parentGroups[groupName]) parentGroups[groupName] = [];
            parentGroups[groupName].push(el);
        } else {
            standalone.push(el);
        }
    });

    standalone.forEach(initElement);

    staggerGroups.forEach(parentGroups => {
        Object.entries(parentGroups).forEach(([groupName, groupElements]) => {
            initStaggerGroup(groupName, groupElements);
        });
    });

    // Clean up orphaned triggers (e.g. from htmx DOM replacements)
    for (let i = triggers.length - 1; i >= 0; i--) {
        if (triggers[i].trigger && !document.contains(triggers[i].trigger)) {
            triggers[i].kill();
            triggers.splice(i, 1);
        }
    }
}

function create() {
    init();
}

function destroy() {
    triggers.forEach(trigger => trigger.kill());
    triggers.length = 0;

    gsap.utils.toArray('[data-scroll-initialized]').forEach(el => {
        gsap.set(el, { clearProps: 'all' });
        el.removeAttribute('data-scroll-initialized');
        el.removeAttribute('data-scroll-complete');
    });
}

function refresh() {
    ScrollTrigger.refresh();
}

const scrollAnimations = { create, init, destroy, refresh };

export default scrollAnimations;
